/* eslint-disable */
import { useEffect, useRef, useState } from "react"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import text from "styles/text"
import media from "styles/media"
import easing from "styles/easing"
import gsap from "gsap/all"
import useMedia from "utils/useMedia"
import { graphql, useStaticQuery } from "gatsby"
import { LocaleData } from "types/gatsby-alias"
import useGetStaticTranslations from "utils/hooks/useGetStaticTranslations"
import { fresponsive } from "utils/fullyResponsive"
import { useI18next } from "gatsby-plugin-react-i18next"

type Props = {
  onClick: () => void
  lightMode?: boolean
  className?: string
  burgerMode?: "none" | "open" | "closed"
  languageOverride?: string
}

const BurgerButton: React.FC<Props> = ({
  onClick,
  className = "",
  lightMode = false,
  burgerMode = "closed",
  languageOverride,
}) => {
  const [hover, setHover] = useState(false)
  const burgerRect1Ref = useRef(null)
  const burgerRect2Ref = useRef(null)
  const burgerRect3Ref = useRef(null)
  const { language } = useI18next()

  const localeDataJS: LocaleData = useGetStaticTranslations(
    "menu",
    languageOverride ?? language
  )

 

  const menuSlideRef = useRef<HTMLDivElement>(null)

  const slideThingWidth = useMedia("113px", "6.807vw", "0", "0")

  useEffect(() => {
    if (menuSlideRef.current) {
      if (hover) {
        gsap.to(menuSlideRef.current, {
          width: slideThingWidth,
          delay: 0,
          duration: 0.3,
          x: -0.01,
        })
      } else {
        gsap.to(menuSlideRef.current, {
          width: 0,
          duration: 0.3,
          x: -0.01,
        })
      }
    }
  }, [hover, slideThingWidth])

  useEffect(() => {
    if (burgerRect1Ref.current === null) {
      return
    }
    const dur = 0.3
    const ease = "1, 0, 0.5, 1"
    const tl = gsap.timeline()
    if (burgerMode === "open") {
      tl.fromTo(
        burgerRect1Ref.current,
        {
          x: "0%",
          y: "300%",
          transformOrigin: "center center",
          rotation: 0,
          duration: dur,
        },
        {
          x: "0%",
          y: "700%",
          transformOrigin: "center center",
          rotation: 135,
          duration: dur,
          ease,
        },
        0
      )
        .fromTo(
          burgerRect2Ref.current,
          {
            rotation: 0,
            opacity: 1,
            duration: dur / 2,
            // ease: ease,
            transformOrigin: "center center",
          },
          {
            rotation: 135,
            opacity: 0,
          },
          0
        )
        .fromTo(
          burgerRect3Ref.current,
          {
            x: "0%",
            y: "1100%",
            // y: "1.024vw",
            transformOrigin: "center center",
            // rotation: 135,
            duration: dur,
            ease,
          },
          {
            x: "0%",
            y: "700%",
            // y: "1.024vw",
            transformOrigin: "center center",
            rotation: -135,
            duration: dur,
          },
          0
        )
    } else {
      tl.to(
        burgerRect1Ref.current,
        {
          x: "0%",
          y: "300%",
          transformOrigin: "center center",
          rotation: 0,
          duration: dur,
        },
        0
      )
        .to(
          burgerRect2Ref.current,
          {
            rotation: 0,
            opacity: 1,
          },
          0
        )
        .to(
          burgerRect3Ref.current,
          {
            x: "0%",
            y: "1100%",
            // y: "1.024vw",
            transformOrigin: "center center",
            rotation: 0,
            duration: dur,
          },
          0
        )
    }
    return () => {
      tl.kill()
    }
  }, [burgerMode])

  return (
    <BurgerBtnEl
      onClick={onClick}
      className={className}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onMouseDown={(e: React.MouseEvent) => {
        e.preventDefault()
      }}
      onMouseUp={(e: React.MouseEvent) => {
        e.preventDefault()
      }}
    >
      <SVGBurgerIcon
        lightMode={lightMode}
        ref1={burgerRect1Ref}
        ref2={burgerRect2Ref}
        ref3={burgerRect3Ref}
      />
      <MenuSlide lightMode={lightMode} ref={menuSlideRef}>
        <MenuSliderText lightMode={lightMode}>
          {burgerMode === "open" ? localeDataJS["close"] : "menu"}
        </MenuSliderText>
      </MenuSlide>
    </BurgerBtnEl>
  )
}
const SVGBurgerIcon = ({
  ref1,
  ref2,
  ref3,
  lightMode = false,
}: {
  ref1: React.RefObject<SVGRectElement>
  ref2: React.RefObject<SVGRectElement>
  ref3: React.RefObject<SVGRectElement>
  lightMode?: boolean
}) => {
  const size = 2
  return (
    <svg viewBox="0 0 30 30">
      <rect
        ref={ref1}
        x={0}
        width={"100%"}
        height={size}
        fill={lightMode ? colors.white : colors.blue400}
        rx={size / 2}
      />
      <rect
        y={14}
        width={"100%"}
        height={size}
        fill={lightMode ? colors.white : colors.blue400}
        rx={size / 2}
        ref={ref2}
      />

      <rect
        ref={ref3}
        y={0}
        x={0}
        width={"100%"}
        height={size}
        fill={lightMode ? colors.white : colors.blue400}
        rx={size / 2}
      />
    </svg>
  )
}

const MenuSlide = styled.div<{ lightMode?: boolean }>`
  ${media.fullWidth} {
    border-radius: 55px;
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
    right: 27.5px;
  }
  ${media.tablet} {
    border-radius: 5.371vw;
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
    right: 2.686vw;
    flex-shrink: 0;
  }
  background-color: ${props =>
    props.lightMode ? colors.white : colors.white};
  height: 100%;
  border-radius: 3.313vw;
  border-bottom-right-radius: 0;
  border-top-right-radius: 0;
  position: absolute;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  z-index: -1;
  right: ${3.313 / 2}vw;
  width: 0;
  transition: 300ms width;
  transition-timing-function: ${easing.button};
  overflow: hidden;
`
const MenuSliderText = styled.div<{ lightMode?: boolean }>`
  ${media.fullWidth} {
    margin-left: 25px;
  }
  ${media.tablet} {
    margin-left: 2.441vw;
  }
  text-transform: capitalize;
  margin-left: 1.506vw;

  color: ${props => (props.lightMode ? colors.blue600 : colors.blue600)};
`

const BurgerBtnEl = styled.button<Props>`
  white-space: pre;
  transition-timing-function: ${easing.button};
  transition: 300ms;
  transition-property: color, background-color;
  -moz-user-select: none;
  -khtml-user-select: none;
  user-select: none;
  ${text.buttonL};
  z-index: 0;
  padding: 0.964vw 1.807vw;
  position: relative;

  background-color: ${({ lightMode }) =>
    lightMode ? colors.blue400 : colors.white};

  border-radius: 3.313vw;
  width: 3.313vw;
  height: 3.313vw;
  cursor: pointer;
  display: flex;

  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  color: ${({ lightMode }) => (lightMode ? colors.white : colors.blue400)};

  padding: 0;
  padding: 8px;

  ${fresponsive(css`
    width: 50px;
    height: 50px;
  `)}

  ${media.mobile} {
    ${text.buttonS};
    width: 10.667vw;
    height: 10.667vw;
    border-radius: 10.667vw;
  }

  path,
  rect {
    transition-timing-function: ${easing.button};
    transition: 300ms;
    transition-property: fill, stroke;
    fill: ${({ lightMode }) => (lightMode ? colors.white : colors.blue400)};
  }

  svg {
    position: relative;
    display: block;
    max-width: 100%;
    max-height: 100%;

    ${fresponsive(css`
      width: 50px;
      height: 50px;
      margin: 0;
      padding: 0;
      box-shadow: none;
    `)}

    ${media.mobile} {
      box-shadow: none;
      width: 6.667vw;
      height: 6.667vw;
      margin: 0;
      padding: 0;
      box-shadow: none;
      margin: 0;
      padding: 0;
    }

    ${media.hover} {
      &:hover {
        background-color: ${({ lightMode }) =>
          lightMode ? colors.blue700 : colors.blue50};
      }
      path {
        fill: ${({ lightMode }) =>
          lightMode ? colors.blue400 : colors.blue50};
      }

      :active {
        color: ${({ lightMode }) =>
          lightMode ? colors.white : colors.blue400};
        background-color: ${({ lightMode }) =>
          lightMode ? colors.blue400 : colors.white};
        path {
          fill: ${({ lightMode }) =>
            lightMode ? colors.blue400 : colors.white};
        }
      }
    }
  }
`

export default BurgerButton
